import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  AlertTriangle, Archive, ArchiveRestore, CheckCircle, ClipboardCheck, Clock, Download, Edit, Eye, FileArchive,
  FileText, HardHat, History, Loader2, Mail, MoreVertical, Plus, RefreshCw, Search, ShieldAlert, ShieldCheck,
  Trash2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../lib/errors';
import SousTraitantFormDialog from '../components/sous-traitants/SousTraitantFormDialog';
import SousTraitantDocumentsTab from '../components/sous-traitants/SousTraitantDocumentsTab';
import SousTraitantControlesTab from '../components/sous-traitants/SousTraitantControlesTab';
import {
  API_URL, CONFORMITE, FORMES_JURIDIQUES, STATUTS_ST, fetchAndDownload, formatDateFr, formatDateTimeFr,
  formatMontant, formatSiret, joursRestantsLabel
} from '../components/sous-traitants/constants';

const FILTERS = [
  { key: 'tous', label: 'Tous' },
  { key: 'conforme', label: 'Conformes' },
  { key: 'a_renouveler', label: 'À renouveler' },
  { key: 'non_conforme', label: 'Non conformes' },
  { key: 'archive', label: 'Archivés' }
];

const SEUIL_VIGILANCE = 5000;

const ACTION_LABELS = {
  creer: 'Création',
  modifier: 'Modification',
  supprimer: 'Suppression',
  valider: 'Validation',
  exporter: 'Export'
};

const RESOURCE_LABELS = {
  sous_traitant: 'Fiche sous-traitant',
  sous_traitant_document: 'Document',
  sous_traitant_controle: 'Contrôle de vigilance',
  sous_traitant_document_types: 'Types de documents'
};

const ConformiteBadge = ({ value }) => {
  const c = CONFORMITE[value] || CONFORMITE.non_conforme;
  return <Badge className={c.color}>{c.label}</Badge>;
};

const StatutBadge = ({ value }) => {
  const s = STATUTS_ST[value];
  if (!s) return null;
  return <Badge className={s.color}>{s.label}</Badge>;
};

// ------------------------------------------------------------------ Dialogue de relance

const RelanceDialog = ({ open, onOpenChange, sousTraitant, onSent }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) setMessage(''); }, [open]);

  if (!sousTraitant) return null;
  const libelles = sousTraitant.libelles || {};
  const attendus = [
    ...(sousTraitant.documents_manquants || []).map((c) => ({ code: c, motif: 'manquant' })),
    ...(sousTraitant.documents_expires || []).map((c) => ({ code: c, motif: 'expiré' })),
    ...(sousTraitant.documents_bientot_expires || []).map((c) => ({ code: c, motif: 'expire bientôt' }))
  ];

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/sous-traitants/${sousTraitant.id}/relancer`, { message: message || null });
      toast.success(res.data?.message || 'Relance envoyée');
      onSent?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Envoi de la relance impossible'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Relancer le sous-traitant par email</DialogTitle>
          <DialogDescription>
            {sousTraitant.contact_email
              ? <>Un email listant les pièces attendues sera envoyé à <span className="font-medium">{sousTraitant.contact_email}</span>.</>
              : <span className="text-red-600">Aucune adresse email de contact : renseignez-la dans la fiche avant de relancer.</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border p-3 text-sm dark:border-slate-700">
            <p className="font-medium mb-1">Pièces qui seront demandées</p>
            {attendus.length === 0 ? (
              <p className="text-slate-500">Aucun document manquant ou à renouveler : rien à relancer.</p>
            ) : (
              <ul className="list-disc pl-5 space-y-0.5">
                {attendus.map((a) => <li key={`${a.code}-${a.motif}`}>{libelles[a.code] || a.code} <span className="text-slate-500">— {a.motif}</span></li>)}
              </ul>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="relance-msg">Message complémentaire (facultatif)</Label>
            <Textarea id="relance-msg" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Précisions, délai souhaité, interlocuteur…" />
          </div>
          {sousTraitant.derniere_relance_at && (
            <p className="text-xs text-slate-500">Dernière relance le {formatDateTimeFr(sousTraitant.derniere_relance_at)}{sousTraitant.nb_relances ? ` (${sousTraitant.nb_relances} au total)` : ''}.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={send} disabled={sending || !sousTraitant.contact_email || attendus.length === 0} className="bg-[#0F5156] hover:bg-[#0F5156]/90">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Envoyer la relance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------------------ Onglet Informations

const InfoItem = ({ label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-sm font-medium break-words">{children || '—'}</p>
  </div>
);

const InformationsTab = ({ st }) => {
  const libelles = st.libelles || {};
  const auDessusSeuil = (st.montant_annuel_estime || 0) >= SEUIL_VIGILANCE;
  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-4 dark:border-slate-700 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#0F5156]" />
          <span className="font-semibold">Conformité</span>
          <ConformiteBadge value={st.conformite} />
          {st.controle_en_retard && <Badge className={CONFORMITE.non_conforme.color}>{st.prochain_controle ? 'Contrôle en retard' : 'Contrôle initial à réaliser'}</Badge>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <InfoItem label="Dernier contrôle">{formatDateFr(st.dernier_controle)}</InfoItem>
          <InfoItem label="Prochain contrôle">
            <span className={st.controle_en_retard ? 'text-red-600' : ''}>{formatDateFr(st.prochain_controle)}</span>
          </InfoItem>
          <InfoItem label="Documents expirant (30 j)">{st.documents_expirant_30j ?? 0}</InfoItem>
          <InfoItem label="Documents requis">{(st.documents_requis || []).length}</InfoItem>
        </div>
        {(st.documents_manquants?.length > 0 || st.documents_expires?.length > 0 || st.documents_bientot_expires?.length > 0) && (
          <div className="text-sm space-y-1">
            {st.documents_manquants?.length > 0 && (
              <p className="text-red-600 flex items-start gap-2"><XCircle className="w-4 h-4 mt-0.5 shrink-0" />Manquants : {st.documents_manquants.map((c) => libelles[c] || c).join(', ')}</p>
            )}
            {st.documents_expires?.length > 0 && (
              <p className="text-red-600 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Expirés : {st.documents_expires.map((c) => libelles[c] || c).join(', ')}</p>
            )}
            {st.documents_bientot_expires?.length > 0 && (
              <p className="text-amber-600 flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 shrink-0" />À renouveler : {st.documents_bientot_expires.map((c) => libelles[c] || c).join(', ')}</p>
            )}
          </div>
        )}
        {auDessusSeuil && st.conformite === 'non_conforme' && (
          <p className="text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 rounded p-2 flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
            Montant annuel au-dessus du seuil de {formatMontant(SEUIL_VIGILANCE)} HT sans dossier complet : risque de solidarité financière (L8222-1).
          </p>
        )}
      </div>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Identification</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <InfoItem label="Raison sociale">{st.raison_sociale}</InfoItem>
          <InfoItem label="Forme juridique">{FORMES_JURIDIQUES[st.forme_juridique] || st.forme_juridique}</InfoItem>
          <InfoItem label="Statut"><StatutBadge value={st.statut} /></InfoItem>
          <InfoItem label="SIRET">{formatSiret(st.siret)}</InfoItem>
          <InfoItem label="SIREN">{st.siren}</InfoItem>
          <InfoItem label="Code NAF">{st.code_naf}</InfoItem>
          <InfoItem label="Domaine d'intervention">{st.domaine_activite}</InfoItem>
          <InfoItem label="Adresse">{[st.adresse, [st.code_postal, st.ville].filter(Boolean).join(' '), st.pays].filter(Boolean).join(', ')}</InfoItem>
          <InfoItem label="Tags">{Array.isArray(st.tags) && st.tags.length > 0 ? st.tags.join(', ') : null}</InfoItem>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Contact</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <InfoItem label="Interlocuteur">{[st.contact_prenom, st.contact_nom].filter(Boolean).join(' ')}</InfoItem>
          <InfoItem label="Email">{st.contact_email}</InfoItem>
          <InfoItem label="Téléphone">{st.contact_telephone}</InfoItem>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Relation commerciale</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <InfoItem label="Début de la relation">{formatDateFr(st.date_debut_relation)}</InfoItem>
          <InfoItem label="Montant annuel estimé (HT)">
            {formatMontant(st.montant_annuel_estime)}
            {auDessusSeuil && <span className="ml-2 text-xs text-amber-600">≥ seuil de vigilance</span>}
          </InfoItem>
          <InfoItem label="Emploie des salariés">{st.emploie_salaries ? 'Oui' : 'Non'}</InfoItem>
          <InfoItem label="Salariés étrangers">{st.salaries_etrangers ? 'Oui' : 'Non'}</InfoItem>
          <InfoItem label="Secteur BTP (décennale)">{st.secteur_btp ? 'Oui' : 'Non'}</InfoItem>
          <InfoItem label="Dernière relance">
            {st.derniere_relance_at ? `${formatDateTimeFr(st.derniere_relance_at)}${st.nb_relances ? ` (${st.nb_relances})` : ''}` : 'Aucune'}
          </InfoItem>
        </div>
      </section>

      {st.notes && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Notes internes</h3>
          <p className="text-sm whitespace-pre-wrap text-slate-600 dark:text-slate-300">{st.notes}</p>
        </section>
      )}

      <p className="text-xs text-slate-400">
        Créé le {formatDateTimeFr(st.created_at)} — dernière mise à jour le {formatDateTimeFr(st.updated_at)}
        {st.archived_at ? ` — archivé le ${formatDateTimeFr(st.archived_at)}` : ''}
      </p>
    </div>
  );
};

// ------------------------------------------------------------------ Onglet Historique (journal d'audit)

const describeLog = (log) => {
  const d = log.details || {};
  const parts = [];
  if (d.action === 'archiver') return 'Sous-traitant archivé';
  if (d.action === 'reactiver') return 'Sous-traitant réactivé';
  if (d.action === 'relancer') return `Relance envoyée à ${d.destinataire || ''}${d.documents?.length ? ` (${d.documents.join(', ')})` : ''}`;
  if (d.action === 'dossier_preuve') return `Dossier de preuve exporté (${d.nb_documents ?? 0} documents, ${d.nb_controles ?? 0} contrôles)`;
  if (log.resource_type === 'sous_traitant_document') {
    if (d.type_code) parts.push(d.type_code);
    if (d.fichier_nom) parts.push(d.fichier_nom);
    if (d.statut_verification) parts.push(`→ ${d.statut_verification}`);
    if (d.methode) parts.push(`(${d.methode})`);
    if (d.remplace_document_id) parts.push('— remplace la version précédente');
  } else if (log.resource_type === 'sous_traitant_controle') {
    if (d.type_controle) parts.push(`contrôle ${d.type_controle}`);
    if (d.resultat) parts.push(`résultat : ${d.resultat}`);
    if (d.documents_manquants?.length) parts.push(`manquants : ${d.documents_manquants.join(', ')}`);
  } else if (log.resource_type === 'sous_traitant') {
    if (Array.isArray(d.champs) && d.champs.length) parts.push(`champs : ${d.champs.join(', ')}`);
    if (d.raison_sociale && log.action === 'creer') parts.push(d.raison_sociale);
  }
  return parts.join(' ');
};

const HistoriqueTab = ({ sousTraitantId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/sous-traitants/${sousTraitantId}/historique`)
      .then((res) => { if (!cancelled) setLogs(res.data?.logs || []); })
      .catch((error) => { if (!cancelled) toast.error(getErrorMessage(error, "Chargement de l'historique impossible")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sousTraitantId]);

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#0F5156]" /></div>;
  }
  if (logs.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">Aucun événement enregistré.</p>;
  }
  return (
    <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-3 space-y-4">
      {logs.map((log) => (
        <li key={log.id} className="ml-5">
          <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-[#0F5156] ring-4 ring-white dark:ring-slate-900" />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{ACTION_LABELS[log.action] || log.action}</span>
            <Badge variant="outline" className="text-[10px]">{RESOURCE_LABELS[log.resource_type] || log.resource_type}</Badge>
            <span className="text-xs text-slate-500">{formatDateTimeFr(log.timestamp)} — {log.user_email}</span>
          </div>
          {describeLog(log) && <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{describeLog(log)}</p>}
        </li>
      ))}
    </ol>
  );
};

// ------------------------------------------------------------------ Page

const SousTraitants = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('sous_traitants_edit');
  const canControl = hasPermission('sous_traitants_control');

  const [dashboard, setDashboard] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous');
  const [search, setSearch] = useState('');
  const [showAlertes, setShowAlertes] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('informations');
  const [relanceTarget, setRelanceTarget] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/sous-traitants/dashboard`);
      setDashboard(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Chargement du tableau de bord impossible'));
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const params = filter === 'archive' ? { statut: 'archive' } : {};
      const res = await axios.get(`${API_URL}/sous-traitants`, { params });
      setList(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Chargement des sous-traitants impossible'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const refreshAll = useCallback(() => { fetchDashboard(); fetchList(); }, [fetchDashboard, fetchList]);

  const loadDetail = useCallback(async (id, { silent = false } = {}) => {
    if (!silent) setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/sous-traitants/${id}`);
      setSelected(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Chargement de la fiche impossible'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetail = (st, tab = 'informations') => {
    setSelected(st);
    setDetailTab(tab);
    loadDetail(st.id);
  };

  const onDetailChanged = () => {
    if (selected) loadDetail(selected.id, { silent: true });
    refreshAll();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((s) => {
      if (filter !== 'tous' && filter !== 'archive' && s.conformite !== filter) return false;
      if (!q) return true;
      const haystack = [
        s.raison_sociale, s.siret, s.siren, s.ville, s.domaine_activite, s.contact_nom, s.contact_prenom,
        s.contact_email, ...(Array.isArray(s.tags) ? s.tags : [])
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q) || haystack.replace(/\s/g, '').includes(q.replace(/\s/g, ''));
    });
  }, [list, filter, search]);

  const handleArchive = async (st) => {
    if (!window.confirm(`Archiver ${st.raison_sociale} ? L'historique et les documents sont conservés.`)) return;
    setBusyAction(st.id);
    try {
      await axios.post(`${API_URL}/sous-traitants/${st.id}/archiver`);
      toast.success('Sous-traitant archivé');
      if (selected?.id === st.id) setSelected(null);
      refreshAll();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Archivage impossible'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleReactivate = async (st) => {
    setBusyAction(st.id);
    try {
      await axios.post(`${API_URL}/sous-traitants/${st.id}/reactiver`);
      toast.success('Sous-traitant réactivé');
      if (selected?.id === st.id) loadDetail(st.id, { silent: true });
      refreshAll();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Réactivation impossible'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleDelete = async (st) => {
    if (!window.confirm(`Supprimer définitivement ${st.raison_sociale} et tout son historique ? Cette action est irréversible.`)) return;
    setBusyAction(st.id);
    try {
      await axios.delete(`${API_URL}/sous-traitants/${st.id}`);
      toast.success('Sous-traitant supprimé définitivement');
      if (selected?.id === st.id) setSelected(null);
      refreshAll();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Suppression impossible'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleDossierPreuve = async (st) => {
    setBusyAction(st.id);
    try {
      await fetchAndDownload(`${API_URL}/sous-traitants/${st.id}/dossier-preuve`);
      toast.success('Dossier de preuve téléchargé');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Génération du dossier de preuve impossible'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/sous-traitants/export`, { params: { format: 'csv' }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `registre_sous_traitants_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Registre exporté');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Export impossible'));
    } finally {
      setExporting(false);
    }
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (st) => { setEditing(st); setFormOpen(true); };

  const onSaved = (saved) => {
    refreshAll();
    if (selected && saved?.id === selected.id) loadDetail(saved.id, { silent: true });
  };

  const alertes = dashboard?.alertes || [];

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]" />
      </div>
    );
  }

  const renderActions = (st, { inSheet = false } = {}) => {
    const isArchived = st.statut === 'archive';
    const busy = busyAction === st.id;
    if (inSheet) {
      return (
        <div className="flex flex-wrap gap-2">
          {canEdit && !isArchived && (
            <Button size="sm" variant="outline" onClick={() => openEdit(st)}><Edit className="w-4 h-4 mr-2" />Modifier</Button>
          )}
          {canEdit && !isArchived && (
            <Button size="sm" variant="outline" onClick={() => setRelanceTarget(st)}><Mail className="w-4 h-4 mr-2" />Relancer par email</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => handleDossierPreuve(st)} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileArchive className="w-4 h-4 mr-2" />}Dossier de preuve
          </Button>
          {canEdit && !isArchived && (
            <Button size="sm" variant="outline" onClick={() => handleArchive(st)} disabled={busy}><Archive className="w-4 h-4 mr-2" />Archiver</Button>
          )}
          {canEdit && isArchived && (
            <Button size="sm" variant="outline" onClick={() => handleReactivate(st)} disabled={busy}><ArchiveRestore className="w-4 h-4 mr-2" />Réactiver</Button>
          )}
        </div>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={busy} data-testid={`st-actions-${st.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => openDetail(st)}><Eye className="w-4 h-4 mr-2" />Voir la fiche</DropdownMenuItem>
          <DropdownMenuItem onClick={() => openDetail(st, 'documents')}><FileText className="w-4 h-4 mr-2" />Documents</DropdownMenuItem>
          <DropdownMenuItem onClick={() => openDetail(st, 'controles')}><ClipboardCheck className="w-4 h-4 mr-2" />Contrôles</DropdownMenuItem>
          {canEdit && !isArchived && <DropdownMenuItem onClick={() => openEdit(st)}><Edit className="w-4 h-4 mr-2" />Modifier</DropdownMenuItem>}
          {canEdit && !isArchived && <DropdownMenuItem onClick={() => setRelanceTarget(st)}><Mail className="w-4 h-4 mr-2" />Relancer par email</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => handleDossierPreuve(st)}><FileArchive className="w-4 h-4 mr-2" />Dossier de preuve (ZIP)</DropdownMenuItem>
          {canEdit && (
            <>
              <DropdownMenuSeparator />
              {!isArchived && <DropdownMenuItem onClick={() => handleArchive(st)}><Archive className="w-4 h-4 mr-2" />Archiver</DropdownMenuItem>}
              {isArchived && <DropdownMenuItem onClick={() => handleReactivate(st)}><ArchiveRestore className="w-4 h-4 mr-2" />Réactiver</DropdownMenuItem>}
              {isArchived && (
                <DropdownMenuItem onClick={() => handleDelete(st)} className="text-red-600 focus:text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />Supprimer définitivement
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sous-traitants-page">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <HardHat className="w-7 h-7 text-[#0F5156]" />Sous-traitants
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Obligation de vigilance du donneur d'ordre (art. L8222-1 et D8222-5 du Code du travail) — pièces à obtenir à la signature puis tous les 6 mois.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={refreshAll} className="gap-2"><RefreshCw className="w-4 h-4" />Actualiser</Button>
          <Button variant="outline" onClick={handleExportCsv} disabled={exporting} className="gap-2">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Export CSV
          </Button>
          {canEdit && (
            <Button onClick={openCreate} className="bg-[#0F5156] hover:bg-[#0F5156]/90 gap-2" data-testid="new-sous-traitant-btn">
              <Plus className="w-4 h-4" />Nouveau sous-traitant
            </Button>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="card-stat cursor-pointer" onClick={() => setFilter('tous')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Actifs</p>
                <p className="text-2xl font-bold">{dashboard?.total_actifs ?? 0}</p>
                {dashboard?.total_suspendus > 0 && <p className="text-xs text-slate-400">{dashboard.total_suspendus} suspendu(s)</p>}
              </div>
              <HardHat className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat cursor-pointer" onClick={() => setFilter('conforme')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Conformes</p>
                <p className="text-2xl font-bold text-emerald-500">{dashboard?.conformes ?? 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat cursor-pointer" onClick={() => setFilter('a_renouveler')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">À renouveler</p>
                <p className="text-2xl font-bold text-amber-500">{dashboard?.a_renouveler ?? 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat cursor-pointer" onClick={() => setFilter('non_conforme')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Non conformes</p>
                <p className="text-2xl font-bold text-red-500">{dashboard?.non_conformes ?? 0}</p>
                {dashboard?.au_dessus_seuil_incomplets > 0 && (
                  <p className="text-xs text-red-500">{dashboard.au_dessus_seuil_incomplets} au-dessus de 5 000 € HT</p>
                )}
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat cursor-pointer" onClick={() => setShowAlertes((v) => !v)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Documents expirant (30 j)</p>
                <p className="text-2xl font-bold text-orange-500">{dashboard?.documents_expirant_30j ?? 0}</p>
                {dashboard?.controles_en_retard > 0 && <p className="text-xs text-red-500">{dashboard.controles_en_retard} contrôle(s) en retard</p>}
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Échéances */}
      {alertes.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-900/40">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Échéances à traiter ({alertes.length})</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowAlertes((v) => !v)}>{showAlertes ? 'Réduire' : 'Tout afficher'}</Button>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <ul className="divide-y divide-amber-200/60 dark:divide-amber-900/40">
              {(showAlertes ? alertes : alertes.slice(0, 5)).map((a, idx) => (
                <li key={`${a.sous_traitant_id}-${a.type}-${a.type_code || idx}`} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    {a.type === 'document_expire' || a.type === 'controle_retard' ? <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                      : a.type === 'document_manquant' ? <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                        : <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                    <button type="button" className="font-medium hover:underline truncate" onClick={() => {
                      const st = list.find((s) => s.id === a.sous_traitant_id) || { id: a.sous_traitant_id, raison_sociale: a.raison_sociale };
                      openDetail(st, a.type === 'controle_retard' ? 'controles' : 'documents');
                    }}>{a.raison_sociale}</button>
                    <span className="text-slate-600 dark:text-slate-300 truncate">— {a.label}</span>
                  </div>
                  <span className={`text-xs ${a.jours_restants !== null && a.jours_restants !== undefined && a.jours_restants < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                    {a.type === 'document_manquant' ? 'à fournir' : a.date ? `${formatDateFr(a.date)} (${joursRestantsLabel(a.jours_restants)})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'outline'}
                  className={filter === f.key ? 'bg-[#0F5156] hover:bg-[#0F5156]/90' : ''} onClick={() => setFilter(f.key)}>
                  {f.label}
                  {f.key !== 'tous' && f.key !== 'archive' && dashboard && (
                    <span className="ml-1.5 text-xs opacity-80">
                      {f.key === 'conforme' ? dashboard.conformes : f.key === 'a_renouveler' ? dashboard.a_renouveler : dashboard.non_conformes}
                    </span>
                  )}
                </Button>
              ))}
            </div>
            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Rechercher (raison sociale, SIRET, ville, domaine…)" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="st-search" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header">Raison sociale</TableHead>
                  <TableHead className="table-header hidden md:table-cell">Forme juridique</TableHead>
                  <TableHead className="table-header hidden lg:table-cell">SIRET</TableHead>
                  <TableHead className="table-header hidden md:table-cell">Domaine</TableHead>
                  <TableHead className="table-header">Conformité</TableHead>
                  <TableHead className="table-header hidden sm:table-cell">Prochain contrôle</TableHead>
                  <TableHead className="table-header hidden lg:table-cell text-center">Docs expirant</TableHead>
                  <TableHead className="table-header text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-[#0F5156]" /></TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      {list.length === 0 && filter !== 'archive'
                        ? <>Aucun sous-traitant enregistré.{canEdit ? ' Cliquez sur « Nouveau sous-traitant » pour commencer.' : ''}</>
                        : 'Aucun sous-traitant ne correspond aux critères'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((st) => {
                    const libelles = st.libelles || {};
                    const problemes = [...(st.documents_manquants || []), ...(st.documents_expires || []), ...(st.documents_bientot_expires || [])]
                      .map((c) => libelles[c] || c);
                    return (
                      <TableRow key={st.id} className="table-row" data-testid={`st-row-${st.id}`}>
                        <TableCell className="table-cell">
                          <button type="button" onClick={() => openDetail(st)} className="text-left">
                            <p className="font-medium hover:underline">{st.raison_sociale}</p>
                            <p className="text-xs text-slate-500">
                              {[st.ville, [st.contact_prenom, st.contact_nom].filter(Boolean).join(' ')].filter(Boolean).join(' — ') || (st.contact_email || '')}
                            </p>
                          </button>
                          {st.statut !== 'actif' && <div className="mt-1"><StatutBadge value={st.statut} /></div>}
                        </TableCell>
                        <TableCell className="table-cell hidden md:table-cell text-sm">{FORMES_JURIDIQUES[st.forme_juridique] || st.forme_juridique || '—'}</TableCell>
                        <TableCell className="table-cell hidden lg:table-cell font-mono text-xs">{formatSiret(st.siret)}</TableCell>
                        <TableCell className="table-cell hidden md:table-cell text-sm">{st.domaine_activite || '—'}</TableCell>
                        <TableCell className="table-cell">
                          <div title={problemes.join(', ')}>
                            <ConformiteBadge value={st.conformite} />
                            {problemes.length > 0 && <p className="text-xs text-slate-500 mt-1 max-w-[220px] truncate">{problemes.join(', ')}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="table-cell hidden sm:table-cell text-sm">
                          {st.prochain_controle ? (
                            <span className={st.controle_en_retard ? 'text-red-600 font-medium' : ''}>{formatDateFr(st.prochain_controle)}{st.controle_en_retard ? ' (retard)' : ''}</span>
                          ) : (
                            <span className="text-red-600 text-xs">Contrôle initial à faire</span>
                          )}
                        </TableCell>
                        <TableCell className="table-cell hidden lg:table-cell text-center">
                          {st.documents_expirant_30j > 0 ? <Badge className={CONFORMITE.a_renouveler.color}>{st.documents_expirant_30j}</Badge> : <span className="text-slate-400">0</span>}
                        </TableCell>
                        <TableCell className="table-cell text-right">{renderActions(st)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Fiche détaillée */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto p-0">
          {selected && (
            <div className="flex flex-col min-h-full">
              <SheetHeader className="p-6 pb-4 border-b dark:border-slate-700 text-left space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <SheetTitle className="text-xl">{selected.raison_sociale}</SheetTitle>
                  {selected.conformite && <ConformiteBadge value={selected.conformite} />}
                  <StatutBadge value={selected.statut} />
                  {detailLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                <SheetDescription>
                  {FORMES_JURIDIQUES[selected.forme_juridique] || selected.forme_juridique || ''}
                  {selected.siret ? ` — SIRET ${formatSiret(selected.siret)}` : ''}
                  {selected.domaine_activite ? ` — ${selected.domaine_activite}` : ''}
                </SheetDescription>
                {renderActions(selected, { inSheet: true })}
              </SheetHeader>
              <div className="p-6">
                <Tabs value={detailTab} onValueChange={setDetailTab}>
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="informations">Informations</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                    <TabsTrigger value="controles">Contrôles</TabsTrigger>
                    <TabsTrigger value="historique"><History className="w-4 h-4 mr-1 hidden sm:inline" />Historique</TabsTrigger>
                  </TabsList>
                  <TabsContent value="informations" className="mt-4">
                    <InformationsTab st={selected} />
                  </TabsContent>
                  <TabsContent value="documents" className="mt-4">
                    <SousTraitantDocumentsTab sousTraitant={selected} canEdit={canEdit && selected.statut !== 'archive'} canControl={canControl && selected.statut !== 'archive'} onChanged={onDetailChanged} />
                  </TabsContent>
                  <TabsContent value="controles" className="mt-4">
                    <SousTraitantControlesTab sousTraitant={selected} canControl={canControl} onChanged={onDetailChanged} />
                  </TabsContent>
                  <TabsContent value="historique" className="mt-4">
                    <HistoriqueTab sousTraitantId={selected.id} />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <SousTraitantFormDialog open={formOpen} onOpenChange={setFormOpen} sousTraitant={editing} onSaved={onSaved} />
      <RelanceDialog open={!!relanceTarget} onOpenChange={(o) => { if (!o) setRelanceTarget(null); }} sousTraitant={relanceTarget}
        onSent={() => { refreshAll(); if (selected && relanceTarget && selected.id === relanceTarget.id) loadDetail(selected.id, { silent: true }); }} />
    </div>
  );
};

export default SousTraitants;
